import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  Select,
  IconButton,
  InputAdornment,
  Autocomplete,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Tabs,
  Tab,
  Tooltip,
  Popover,
  Stack
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, Search as SearchIcon, Edit as EditIcon, Check as CheckIcon, Delete as DeleteIcon, ClearAll, FilterList } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Page from '../../../Page';
import { tasksApi } from '../../../../utils/tasksApi';
import { showToast } from '../../../../utils/Toast/SnackbarProvider';
import UserInfo from '../../../../utils/Authorization/UserInfo';
import { getDDMMYYYYFormat } from 'src/utils/commonUtils';
import { debounce } from 'src/utils/debounceSearch';

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'isNew' && prop !== 'dueStatus' && prop !== 'isCompleted'
})(({ theme, isNew, dueStatus, isCompleted }) => {
  let backgroundColor, hoverColor, borderColor;
  
  // If task is completed, use white background
  if (isCompleted) {
    backgroundColor = '#ffffff';
    hoverColor = '#fafafa';
    borderColor = '#e0e0e0';
  } else {
    switch (dueStatus) {
      case 'overdue':
        backgroundColor = '#ffebee'; // Light red
        hoverColor = '#ffcdd2';
        borderColor = '#ffcdd2';
        break;
      case 'due-soon':
        backgroundColor = '#fff3e0'; // Light orange
        hoverColor = '#ffe0b2';
        borderColor = '#ffe0b2';
        break;
      case 'on-time':
        backgroundColor = '#e8f5e8'; // Light green
        hoverColor = '#c8e6c9';
        borderColor = '#c8e6c9';
        break;
      default:
        backgroundColor = '#f5f5f5'; // Light gray for no due date
        hoverColor = '#eeeeee';
        borderColor = '#eeeeee';
    }
  }
  
  return {
    backgroundColor,
    '&:hover': {
      backgroundColor: hoverColor
    },
    '& .MuiTableCell-root': {
      backgroundColor,
      borderBottom: `1px solid ${borderColor}`
    }
  };
});

const DoneButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#4caf50',
  color: 'white',
  fontSize: '0.75rem',
  padding: '4px 12px',
  '&:hover': {
    backgroundColor: '#45a049'
  }
}));

export default function StageTaskManagement() {
  const userInfo = UserInfo();
  const navigate = useNavigate();
  const isSuperAdmin = userInfo?.role?.includes('SUPER_ADMIN');
  
  const [tasks, setTasks] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [stages, setStages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isTeamLead, setIsTeamLead] = useState(false);
  const [currentTab, setCurrentTab] = useState(0); // 0: All, 1: Self created, 2: Assigned to me, 3: Assigned by me
  const [confirmDialog, setConfirmDialog] = useState({ open: false, task: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, task: null });
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTask, setEditTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    company: '',
    stage: ''
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [filterCompanySearch, setFilterCompanySearch] = useState('');
  const [filteredFilterCompanies, setFilteredFilterCompanies] = useState([]);
  const [quickAddCompanySearch, setQuickAddCompanySearch] = useState('');
  const [filteredQuickAddCompanies, setFilteredQuickAddCompanies] = useState([]);
  const [selectedQuickAddCompany, setSelectedQuickAddCompany] = useState(null);
  const [quickAddTask, setQuickAddTask] = useState({
    title: '',
    stage: '',
    due_date: '',
    assigned_to: [],
    assigned_team: '',
    assignment_type: 'user' // 'user' or 'team'
  });
  const [filters, setFilters] = useState({
    search: '',
    user: '',
    company: '',
    stage: '',
    team: '',
    startDate: '',
    endDate: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'To Do',
    company: '',
    stage: '',
    assigned_to: [],
    assigned_team: '',
    assignment_type: 'user',
    due_date: ''
  });

  useEffect(() => {
    // Wait until user info is loaded
    if (!userInfo?.pk) return;

    // Load stages, companies, teams, and team members
    fetchInitialData();

    // Set default assignee for non-super-admin and non-team-lead users
    if (!isSuperAdmin && !isTeamLead) {
      setQuickAddTask(prev => ({ ...prev, assigned_to: [userInfo.pk] }));
    }
  }, [userInfo?.pk, isSuperAdmin, isTeamLead]);

  useEffect(() => {
    // Set default assignment type based on current tab
    if (currentTab === 1 || currentTab === 2) { 
      // Self created and Assigned to me tabs - always user assignment with current user pre-filled
      setQuickAddTask(prev => ({ 
        ...prev, 
        assignment_type: 'user', 
        assigned_to: userInfo?.pk ? [userInfo.pk] : [], 
        assigned_team: '' 
      }));
    } else { 
      // All and Assigned by me tabs - user assignment
      setQuickAddTask(prev => ({ 
        ...prev, 
        assignment_type: 'user', 
        assigned_to: [], 
        assigned_team: '' 
      }));
    }
  }, [currentTab, userInfo?.pk]);

  useEffect(() => {
    // Fetch all users once (no team/tab filtering needed)
    if (userInfo?.pk) {
      fetchTeamMembers();
    }
  }, [userInfo?.pk]);

  // Removed auto-filter for assigned_to - user must explicitly select from dropdown

  useEffect(() => {
    // Reset pagination when filters/tab/company change
    setPage(1);
    setHasMore(true);
    fetchTasks(1, false);
  }, [filters, currentTab, selectedQuickAddCompany?.id]);

  const handleLoadMore = () => {
    if (isLoadingMore || !hasMore) return;
    fetchTasks(page + 1, true);
  };

  const handleScroll = (event) => {
    const target = event.currentTarget;
    if (!target) return;
    const { scrollTop, scrollHeight, clientHeight } = target;
    // When user scrolls within 200px of bottom, try to load more
    if (scrollHeight - scrollTop - clientHeight < 200) {
      handleLoadMore();
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (companySearch.length > 2) {
        const filtered = companies.filter(company => 
          company.name.toLowerCase().includes(companySearch.toLowerCase())
        ).slice(0, 50);
        setFilteredCompanies(filtered);
      } else {
        setFilteredCompanies([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [companySearch, companies]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (filterCompanySearch.length > 0) {
        const filtered = companies.filter(company => 
          company.name.toLowerCase().includes(filterCompanySearch.toLowerCase())
        ).slice(0, 50);
        setFilteredFilterCompanies(filtered);
      } else {
        setFilteredFilterCompanies(companies.slice(0, 50));
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filterCompanySearch, companies]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (quickAddCompanySearch.length > 0) {
        const filtered = companies.filter(company => 
          company.name.toLowerCase().includes(quickAddCompanySearch.toLowerCase())
        ).slice(0, 50);
        setFilteredQuickAddCompanies(filtered);
      } else {
        setFilteredQuickAddCompanies(companies.slice(0, 50));
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [quickAddCompanySearch, companies]);

  // // Filter tasks for non-admin users by their own tasks
  // useEffect(() => {
  //   if (!userInfo?.role || !teamMembers?.length) return;
  //   const isAdmin = userInfo?.role?.includes('SUPER_ADMIN');
  //   if (!isAdmin) {
  //     const loggedInMember = teamMembers.filter(member => member.pk === userInfo.pk);
  //     if (loggedInMember.length === 0) return;
  //     setSelectedFilterUser(loggedInMember?.[0]);
  //     setFilters(prev => ({ ...prev, user: loggedInMember?.[0]?.pk || '' }));
  //   }
  // }, [userInfo, teamMembers]);

  const fetchInitialData = async () => {
    try {
      const [stagesRes, companiesRes, teamsRes] = await Promise.all([
        tasksApi.getStages(),
        tasksApi.getCompanies(),
        tasksApi.getTeams()
      ]);

      // stages & companies are in response.data
      setStages(stagesRes?.data || []);
      setCompanies(companiesRes?.data || []);

      // ✅ teams data is in response.data (NOT .data.data)
      // but we guard for both, just in case
      const teamsData = Array.isArray(teamsRes?.data)
        ? teamsRes.data
        : Array.isArray(teamsRes?.data?.data)
          ? teamsRes.data.data
          : [];
      setTeams(teamsData);

      // Always fetch all users for task assignment dropdown
      await fetchTeamMembers();
    } catch (error) {
      showToast('Error loading data', 'error');
    }
  };

  const checkTeamLeadStatus = async () => {
    try {
      // Only called for non-SUPER_ADMIN users
      const response = await tasksApi.checkTeamLeadStatus();
      const isLead = !!response?.data?.is_team_lead;

      if (isLead) {
        setIsTeamLead(true);
        // backend returns: {"is_team_lead": True, "teams": [{id, name}, ...]}
      } else {
        setIsTeamLead(false);
      }

      // Fetch team members AFTER we know if the user is a lead
      await fetchTeamMembers(isLead);
    } catch (error) {
      setIsTeamLead(false);
      // Still try to fetch team members as "not a lead"
      await fetchTeamMembers(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      if (!userInfo?.pk) return;

      const membersRes = await tasksApi.getAllTeamMembers();

      const rows = Array.isArray(membersRes?.data?.data)
        ? membersRes.data.data
        : Array.isArray(membersRes?.data)
          ? membersRes.data
          : [];

      const users = rows
        .map(item => item?.user || item)
        .filter(u => u && u.pk);

      setTeamMembers(users);
    } catch (error) {
      showToast('Error loading team members', 'error');

      // Graceful fallback: only current user
      if (userInfo?.pk) {
        setTeamMembers([{
          pk: userInfo.pk,
          username: userInfo.username,
          first_name: userInfo.first_name,
          last_name: userInfo.last_name,
          is_active: true
        }]);
      }
    }
  };

  const fetchTasks = async (pageToLoad = 1, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const serverFilters = {};
      
      if (filters.search) serverFilters.search = filters.search;
      if (filters.user) serverFilters.assigned_to = filters.user;
      if (filters.company) serverFilters.company_id = filters.company;
      if (filters.stage) serverFilters.stage_id = filters.stage;
      if (filters.team) serverFilters.assigned_team = filters.team;
      if (filters.startDate) serverFilters.start_date = filters.startDate;
      if (filters.endDate) serverFilters.end_date = filters.endDate;
      // Pagination params
      serverFilters.page = pageToLoad;
      serverFilters.page_size = 10;
      // If team filter is an array, send comma-separated IDs; otherwise send scalar
      if (Array.isArray(filters.team) && filters.team.length) {
        serverFilters.assigned_team = filters.team.join(',');
      }
      
      // Handle tab-based filtering
      // Tab indices: 0=All, 1=Self created, 2=Assigned to me, 3=Assigned by me
      // For super admin: 4=Created by Others, 5=Done Tasks
      // For non-super admin: 4=Done Tasks
      if (currentTab === 0) {
        // All - backend excludes done tasks by default
      } else if (currentTab === 1) {
        // Self created
        serverFilters.type = 'self';
      } else if (currentTab === 2) {
        // Assigned to me
        serverFilters.type = 'to_me';
      } else if (currentTab === 3) {
        // Assigned by me
        serverFilters.type = 'by_me';
      } else if (currentTab === 4 && isSuperAdmin) {
        // Created by Others (super admin only)
        serverFilters.type = 'others';
      } else if ((currentTab === 5 && isSuperAdmin) || (currentTab === 4 && !isSuperAdmin)) {
        // Done tasks
        serverFilters.type = 'done';
      }
  
      const response = await tasksApi.getAllTasks(serverFilters);
      const data = response.data;

      let newTasks = [];
      let nextExists = false;

      if (Array.isArray(data)) {
        // Backward compatibility if API ever returned a plain list
        newTasks = data;
        nextExists = false;
      } else {
        newTasks = data.results || [];
        nextExists = !!data.next;
      }

      setTasks(prev => (append ? [...prev, ...newTasks] : newTasks));
      setPage(pageToLoad);
      setHasMore(nextExists);
    } catch (error) {
      showToast('Error fetching tasks', 'error');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };


  const handleCreateTask = async () => {
    try {
      const taskData = { ...newTask };
      if (taskData.due_date) {
        taskData.due_date = new Date(taskData.due_date + 'T23:59:59').toISOString();
      }
      // Backend expects a list of user IDs for many-to-many assigned_to
      if (!Array.isArray(taskData.assigned_to)) {
        taskData.assigned_to = taskData.assigned_to ? [taskData.assigned_to] : [];
      }
      await tasksApi.createTask(taskData);
      setOpenDialog(false);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        status: 'To Do',
        company: '',
        stage: '',
        assigned_to: (isSuperAdmin || isTeamLead) ? [] : (userInfo?.pk ? [userInfo.pk] : []),
        due_date: ''
      });
      setSelectedCompany(null);
      setCompanySearch('');
      fetchTasks();
      showToast('Task created successfully', 'success');
    } catch (error) {
      showToast('Error creating task', 'error');
    }
  };

  const handleMarkAsDone = async (task) => {
    try {
      await tasksApi.updateTaskStatus(task.id, 'Done');
      setConfirmDialog({ open: false, task: null });
      fetchTasks();
      showToast('Task marked as done', 'success');
    } catch (error) {
      showToast('Error updating task', 'error');
    }
  };

  const handleEditTask = (task) => {
    setEditTask({
      title: task.title,
      description: task.description || '',
      priority: task.priority || 'medium',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      company: task.company,
      stage: task.stage
    });
    setEditingTaskId(task.id);
  };

  const handleUpdateTask = async (taskId) => {
    try {
      const taskData = { ...editTask };
      if (taskData.due_date) {
        taskData.due_date = new Date(taskData.due_date + 'T23:59:59').toISOString();
      }
      await tasksApi.updateTask(taskId, taskData);
      setEditingTaskId(null);
      fetchTasks();
      showToast('Task updated successfully', 'success');
    } catch (error) {
      showToast('Error updating task', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditTask({
      title: '',
      description: '',
      priority: 'medium',
      due_date: '',
      company: '',
      stage: ''
    });
  };

  const canEditTask = (task) => {
    return task.created_by === userInfo?.pk;
  };

  const isTaskAssignee = (task) => {
    if (!task) return false;

    const currentUserId = userInfo?.pk;
    if (!currentUserId) return false;

    // assigned_to is a list of user IDs from backend many-to-many field
    if (Array.isArray(task.assigned_to)) {
      return task.assigned_to.includes(currentUserId);
    }

    return task.assigned_to === currentUserId;
  };

  const handleDeleteTask = async (task) => {
    try {
      await tasksApi.deleteTask(task.id);
      setDeleteDialog({ open: false, task: null });
      fetchTasks();
      showToast('Task deleted successfully', 'success');
    } catch (error) {
      showToast('Error deleting task', 'error');
    }
  };

  const isTaskNew = (createdDate) => {
    const now = new Date();
    const created = new Date(createdDate);
    const diffHours = (now - created) / (1000 * 60 * 60);
    return diffHours < 24;
  };

  const getDueStatus = (dueDate) => {
    if (!dueDate) return 'no-date';
    
    const now = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'overdue';
    if (diffDays <= 3) return 'due-soon';
    return 'on-time';
  };

  const handleQuickAddTask = async () => {
    const hasUserAssignment = quickAddTask.assignment_type === 'user'
      ? Array.isArray(quickAddTask.assigned_to) && quickAddTask.assigned_to.length > 0
      : false;
    const hasTeamAssignment = quickAddTask.assignment_type === 'team' ? !!quickAddTask.assigned_team : false;
    const hasAssignment = hasUserAssignment || hasTeamAssignment;
    
    if (!quickAddTask.title || !quickAddTask.due_date || !hasAssignment) {
      showToast('Please fill all required fields', 'error');
      return;
    }

    try {
      const taskData = {
        title: quickAddTask.title,
        due_date: quickAddTask.due_date ? new Date(quickAddTask.due_date + 'T23:59:59').toISOString() : null,
        priority: 'medium',
        status: 'To Do'
      };

      if (selectedQuickAddCompany?.id) taskData.company = selectedQuickAddCompany.id;
      if (quickAddTask.stage) taskData.stage = quickAddTask.stage;

      if (quickAddTask.assignment_type === 'user') {
        // Always send a list to match many-to-many assigned_to on backend
        taskData.assigned_to = Array.isArray(quickAddTask.assigned_to)
          ? quickAddTask.assigned_to
          : (quickAddTask.assigned_to ? [quickAddTask.assigned_to] : []);
      } else {
        taskData.assigned_team = quickAddTask.assigned_team;
      }
      
      await tasksApi.createTask(taskData);
      
      // Reset form
      setQuickAddTask({
        title: '',
        stage: '',
        due_date: '',
        assigned_to: [],
        assigned_team: '',
        assignment_type: 'user'
      });
      setSelectedQuickAddCompany(null);
      setQuickAddCompanySearch('');
      
      fetchTasks();
      showToast('Task created successfully', 'success');
    } catch (error) {
      showToast('Error creating task', 'error');
    }
  };



  const getAssigneeName = (task) => {
    if (task.assigned_team_name) {
      return `Team: ${task.assigned_team_name}`;
    }
    if (Array.isArray(task.assigned_to_details) && task.assigned_to_details.length) {
      return task.assigned_to_details
        .map(user => `${user.first_name || ''} ${user.last_name || ''}`.trim())
        .join(', ');
    }
    return 'Unassigned';
  };

  const getStageName = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage ? stage.name : 'Unknown';
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const filteredTasks = tasks.sort((a, b) => {
    // Apply custom sorting if configured
    if (sortConfig.key === 'due_date') {
      const aDate = a.due_date ? new Date(a.due_date) : null;
      const bDate = b.due_date ? new Date(b.due_date) : null;
      
      // Handle null dates (put them at the end)
      if (!aDate && !bDate) return 0;
      if (!aDate) return sortConfig.direction === 'asc' ? 1 : -1;
      if (!bDate) return sortConfig.direction === 'asc' ? -1 : 1;
      
      const comparison = aDate - bDate;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    }
    
    // Default sorting: completion date for done tasks, creation date for others
    if (a.status === 'Done' && b.status === 'Done' && a.completed_at && b.completed_at) {
      return new Date(b.completed_at) - new Date(a.completed_at);
    }
    return 0;
  });

  const debouncedSearch = debounce((query) => {
    setFilters({ ...filters, search: query })
  });

  return (
    <Container sx={{ minHeight: '100%', maxHeight: '100%', minWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0, m: 0 }}>
      {/* Sticky Header Section */}
      <Box sx={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 10, 
        bgcolor: 'background.paper',
      }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3, pt: 3 }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Task Backlog
          </Typography>

          {/* Tabs */}
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
          >
            <Tab label="All" />
            <Tab label="Self created" />
            <Tab label="Assigned to me" />
            <Tab label="Assigned by me" />
            {isSuperAdmin && <Tab label="Created by Others" />}
            <Tab label="Done Tasks" />
          </Tabs>
        </Stack>

        {/* Filter Section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
          {/* Row 1: Applied Filters */}
          {(filters.user ||
            filters.company ||
            filters.stage ||
            filters.team ||
            filters.startDate ||
            filters.endDate) && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              {filters.user && (
                <Chip
                  label={`User: ${
                    teamMembers?.find((u) => u.pk === filters.user)
                      ? `${
                          teamMembers.find((u) => u.pk === filters.user)
                            .first_name
                        } ${
                          teamMembers.find((u) => u.pk === filters.user)
                            .last_name
                        }`
                      : ""
                  }`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.company && (
                <Chip
                  label={`Company: ${
                    companies.find((c) => c.id === filters.company)?.name || ""
                  }`}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.stage && (
                <Chip
                  label={`Stage: ${
                    stages.find((s) => s.id === filters.stage)?.name || ""
                  }`}
                  color="success"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.team && (
                <Chip
                  label={`Team: ${
                    teams.find((t) => t.id === filters.team)?.name || ""
                  }`}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.startDate && (
                <Chip
                  label={`From: ${filters.startDate}`}
                  color="default"
                  variant="outlined"
                  size="small"
                />
              )}
              {filters.endDate && (
                <Chip
                  label={`To: ${filters.endDate}`}
                  color="default"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          )}

          {/* Row 2: Search + Right Icons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* 🔍 Search and Filters in Same Row */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
            >
              {/* Search */}
              <Box sx={{ flex: 1, maxWidth: 300 }}>
                <TextField
                  size="small"
                  placeholder="Search tasks..."
                  defaultValue={filters.search}
                  onChange={(e) => debouncedSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Box>

              {/* Autocomplete for User */}
              <Autocomplete
                sx={{ minWidth: 150 }}
                size="small"
                options={teamMembers || []}
                getOptionLabel={(o) => o ? `${o.first_name || ''} ${o.last_name || ''}` : ''}
                value={teamMembers?.find((u) => u?.pk === filters.user) || null}
                onChange={(e, val) =>
                  setFilters({ ...filters, user: val?.pk || "" })
                }
                isOptionEqualToValue={(option, value) => option?.pk === value?.pk}
                renderInput={(params) => <TextField {...params} label="User" />}
              />

              {/* Autocomplete for Team */}
              <Autocomplete
                sx={{ minWidth: 200 }}
                size="small"
                options={teams}
                getOptionLabel={(o) => o.name}
                value={teams.find((t) => t.id === filters.team) || null}
                onChange={(e, val) =>
                  setFilters({ ...filters, team: val?.id || "" })
                }
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => <TextField {...params} label="Team" />}
              />

              {/* Autocomplete for Stage */}
              <Autocomplete
                sx={{ minWidth: 200 }}
                size="small"
                options={stages}
                getOptionLabel={(o) => o.name}
                value={stages.find((s) => s.id === filters.stage) || null}
                onChange={(e, val) =>
                  setFilters({ ...filters, stage: val?.id || "" })
                }
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => (
                  <TextField {...params} label="Stage" />
                )}
              />
            </Box>

            {/* Date Filters */}
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
              <TextField
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />

              <TextField
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        setFilters({
                            search: '',
                            user: '',
                            company: '',
                            stage: '',
                            team: '',
                            startDate: '',
                            endDate: '',
                        });
                    }}
                >
                    Clear
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* End Sticky Header Section */}

      {/* Scrollable Task Table Section */}
      <Box sx={{ 
        flex: 1,
        flexGrow: 1,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}
        onScroll={handleScroll}
      >
        <TableContainer component={Paper} sx={{ boxShadow: 'none', height: '100%' }}>
          <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5", borderBottom: '2px solid #e0e0e0', width: '14%' }}>COMPANY</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5", borderBottom: '2px solid #e0e0e0', width: '11%' }}>STAGE</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5", borderBottom: '2px solid #e0e0e0', width: '22%' }}>TASK</TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#f5f5f5",
                    borderBottom: '2px solid #e0e0e0',
                    cursor: "pointer",
                    userSelect: "none",
                    width: '11%',
                    "&:hover": { bgcolor: "#e8e8e8" },
                  }}
                  onClick={() => handleSort("due_date")}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    DUE DATE
                    <Box component="span" sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                      {getSortIcon("due_date")}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5", borderBottom: '2px solid #e0e0e0', textAlign: "center", width: '13%' }}>
                  ASSIGNED BY
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5", borderBottom: '2px solid #e0e0e0', textAlign: "center", width: '13%' }}>
                  ASSIGNEE
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f5f5f5", borderBottom: '2px solid #e0e0e0', textAlign: "center", width: '16%', minWidth: '140px' }}>
                  ACTIONS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ width: "100%" }}>
              {tasks.length ? (
                tasks.map((task) =>
                  editingTaskId === task.id ? (
                    <TableRow
                      key={`edit-${task.id}`}
                      sx={{ bgcolor: "#f8f9fa" }}
                    >
                      <TableCell>
                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 150 }}
                          options={companies}
                          getOptionLabel={(option) => option.name || ""}
                          value={
                            companies.find((c) => c.id === editTask.company) ||
                            null
                          }
                          onChange={(event, newValue) => {
                            setEditTask({
                              ...editTask,
                              company: newValue?.id || "",
                            });
                          }}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Company"
                              variant="outlined"
                            />
                          )}
                          noOptionsText="No companies found"
                        />
                      </TableCell>
                      <TableCell>
                        {/* <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={editTask.stage}
                              onChange={(e) => setEditTask({ ...editTask, stage: e.target.value })}
                              placeholder='Stage'
                            >
                              {stages.map((stage) => (
                                <MenuItem key={stage.id} value={stage.id}>
                                  {stage.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl> */}
                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 120 }}
                          options={stages}
                          getOptionLabel={(option) => option.name}
                          value={
                            stages.find((s) => s.id === editTask.stage) || null
                          }
                          onChange={(event, newValue) => {
                            setEditTask({
                              ...editTask,
                              stage: newValue?.id || "",
                            });
                          }}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Stage"
                              variant="outlined"
                            />
                          )}
                          noOptionsText="No stages found"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={editTask.title}
                          onChange={(e) =>
                            setEditTask({ ...editTask, title: e.target.value })
                          }
                          sx={{ minWidth: 200 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="date"
                          value={editTask.due_date}
                          onChange={(e) =>
                            setEditTask({
                              ...editTask,
                              due_date: e.target.value,
                            })
                          }
                          sx={{ minWidth: 140 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Tooltip
                            title={
                              task.created_by_details
                                ? `${task.created_by_details.first_name} ${task.created_by_details.last_name}`
                                : "Unknown"
                            }
                            arrow
                          >
                            {/* <Avatar sx={{ width: 24, height: 24, bgcolor: '#ff9800', fontSize: '0.75rem' }}>
                                {task.created_by_details ? task.created_by_details.first_name.charAt(0) : 'U'}
                              </Avatar> */}
                            <Typography variant="body2">
                              {task.created_by_details
                                ? `${task.created_by_details.first_name} ${task.created_by_details.last_name}`
                                : "Unknown"}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "left",
                          }}
                        >
                          <Tooltip title={getAssigneeName(task)} arrow>
                            {/* <Avatar sx={{ width: 24, height: 24, bgcolor: '#1976d2', fontSize: '0.75rem' }}>
                                {getAssigneeName(task).charAt(0)}
                              </Avatar> */}
                            <Typography variant="body2">
                              {getAssigneeName(task)}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleUpdateTask(task.id)}
                            disabled={!editTask.title}
                            sx={{
                              bgcolor: "#28a745",
                              "&:hover": { bgcolor: "#218838" },
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <StyledTableRow
                      key={task.id}
                      isNew={isTaskNew(task.created_at)}
                      dueStatus={getDueStatus(task.due_date)}
                      isCompleted={task.status === 'Done'}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 40,
                              bgcolor: "#ff5722",
                              borderRadius: 1,
                            }}
                          />
                          {task?.company_name && task?.company ? (
                            <Typography
                              variant="body2"
                              onClick={() => navigate(`/companies/${task.company}`)}
                              sx={{
                                cursor: 'pointer',
                                color: '#1976d2',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {task.company_name}
                            </Typography>
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {task?.stage ? getStageName(task.stage) : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {task.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "#666", whiteSpace: "nowrap" }}
                        >
                          {getDDMMYYYYFormat(task.due_date) || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <Tooltip
                            title={
                              task.created_by_details
                                ? `${task.created_by_details.first_name} ${task.created_by_details.last_name}`
                                : "Unknown"
                            }
                            arrow
                          >
                            {/* <Avatar sx={{ width: 24, height: 24, bgcolor: '#ff9800', fontSize: '0.75rem' }}>
                                {task.created_by_details ? task.created_by_details.first_name.charAt(0) : 'U'}
                              </Avatar> */}
                            <Typography variant="body2">
                              {task.created_by_details
                                ? `${task.created_by_details.first_name} ${task.created_by_details.last_name}`
                                : "Unknown"}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <Tooltip title={getAssigneeName(task)} arrow>
                            {/* <Avatar sx={{ width: 24, height: 24, bgcolor: '#1976d2', fontSize: '0.75rem' }}>
                                {getAssigneeName(task).charAt(0)}
                              </Avatar> */}
                            <Typography variant="body2">
                              {getAssigneeName(task)}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {canEditTask(task) && task.status !== "Done" && (
                            <IconButton
                              size="small"
                              onClick={() => handleEditTask(task)}
                              sx={{ color: "#1976d2" }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          {canEditTask(task) && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                setDeleteDialog({ open: true, task })
                              }
                              sx={{ color: "#d32f2f" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                          {task.status !== "Done" && isTaskAssignee(task) ? (
                            <DoneButton
                              size="small"
                              onClick={() =>
                                setConfirmDialog({ open: true, task })
                              }
                            >
                              Done
                            </DoneButton>
                          ) : task.status === "Done" ? (
                            <IconButton
                              size="small"
                              sx={{ color: "#000", bgcolor: "transparent" }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Box>
                      </TableCell>
                    </StyledTableRow>
                  )
                )
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography
                      variant="body2"
                      sx={{ p: 3, color: "#666", textAlign: "center" }}
                    >
                      No tasks exists.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Infinite scroll loading indicator */}
        {isLoadingMore && (
          <Box sx={{ py: 1.5, textAlign: 'center', color: '#666', fontSize: '0.8rem' }}>
            Loading more tasks...
          </Box>
        )}
      </Box>
      {/* End Scrollable Task Table Section */}

      {/* Sticky Add Task Row */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          bgcolor: "#ffffff",
          p: 1.5,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Autocomplete
            size="small"
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                "&:hover": {
                  borderColor: "#adb5bd",
                },
              },
            }}
            options={filteredQuickAddCompanies}
            getOptionLabel={(option) => option.name || ""}
            value={selectedQuickAddCompany}
            onChange={(event, newValue) => {
              setSelectedQuickAddCompany(newValue);
            }}
            inputValue={quickAddCompanySearch}
            onInputChange={(event, newInputValue) => {
              setQuickAddCompanySearch(newInputValue);
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Company"
                variant="outlined"
              />
            )}
            noOptionsText="No companies found"
          />

          <Autocomplete
            size="small"
            sx={{
              minWidth: 120,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                "&:hover": {
                  borderColor: "#adb5bd",
                },
              },
            }}
            options={stages}
            getOptionLabel={(option) => option.name}
            value={stages.find((s) => s.id === quickAddTask.stage) || null}
            onChange={(event, newValue) => {
              setQuickAddTask({
                ...quickAddTask,
                stage: newValue?.id || "",
              });
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Stage"
                variant="outlined"
              />
            )}
            noOptionsText="No stages found"
          />

          <TextField
            size="small"
            placeholder="Task title..."
            value={quickAddTask.title}
            onChange={(e) =>
              setQuickAddTask({ ...quickAddTask, title: e.target.value })
            }
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                "&:hover": {
                  borderColor: "#adb5bd",
                },
              },
            }}
            variant="outlined"
          />

          <Tooltip title="Due Date (dd/mm/yyyy)" arrow>
            <TextField
              size="small"
              type="date"
              placeholder="dd/mm/yyyy"
              value={quickAddTask.due_date}
              onChange={(e) =>
                setQuickAddTask({
                  ...quickAddTask,
                  due_date: e.target.value,
                })
              }
              sx={{
                minWidth: 140,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#adb5bd",
                  },
                },
              }}
              variant="outlined"
            />
          </Tooltip>

          {/* <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select 
              displayEmpty 
              value={quickAddTask.assignment_type}
              onChange={(e) => setQuickAddTask({ ...quickAddTask, assignment_type: e.target.value, assigned_to: '', assigned_team: '' })}
              sx={{
                bgcolor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                '&:hover': {
                  borderColor: '#adb5bd'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }}
            >
              <MenuItem value="user">User👤</MenuItem>
              <MenuItem value="team">Team👥</MenuItem>
            </Select>
          </FormControl> */}

          {/* User / Team assignment selector
              - User: multi-select (array of user IDs)
              - Team: single-select team ID */}
          {quickAddTask.assignment_type === 'user' ? (
            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 160 }}
              options={teamMembers}
              getOptionLabel={(member) =>
                member ? `${member.first_name} ${member.last_name}` : ''
              }
              value={Array.isArray(quickAddTask.assigned_to)
                ? teamMembers.filter(m => quickAddTask.assigned_to.includes(m.pk))
                : []}
              onChange={(event, newValue) => {
                const ids = Array.isArray(newValue) ? newValue.map(m => m.pk) : [];
                setQuickAddTask({
                  ...quickAddTask,
                  assigned_to: ids,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Users"
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      '&:hover': {
                        borderColor: '#adb5bd',
                      },
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                />
              )}
            />
          ) : (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                displayEmpty
                value={quickAddTask.assigned_team}
                onChange={(e) =>
                  setQuickAddTask({ ...quickAddTask, assigned_team: e.target.value })
                }
                disabled={!isSuperAdmin}
                sx={{
                  bgcolor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#adb5bd",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                <MenuItem value="" sx={{ color: "#6c757d" }}>
                  Team
                </MenuItem>
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleQuickAddTask}
            disabled={
              !quickAddTask.title ||
              // Require at least one assignee based on assignment_type
              (
                quickAddTask.assignment_type === 'user'
                  ? !(Array.isArray(quickAddTask.assigned_to) && quickAddTask.assigned_to.length > 0)
                  : !quickAddTask.assigned_team
              )
            }
            sx={{
              bgcolor: "#28a745",
              color: "white",
              px: 3,
              py: 1,
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "4px",
              "&:hover": {
                bgcolor: "#218838",
              },
              "&:disabled": {
                bgcolor: "#6c757d",
                color: "white",
              },
            }}
          >
            Add
          </Button>
        </Box>
        
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" sx={{ mt: 1.5, color: "#666" }}>
            Showing {tasks.length} of {tasks.length} tasks
          </Typography>

          {/* Note: Select Company to create company related tasks */}
          <Typography variant="body2" sx={{ mt: 1.5, color: "#cf7725ff" }}>
            * To create company related tasks, select a company first.
          </Typography>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, task: null })}
      >
        <DialogTitle>Mark Task as Done</DialogTitle>
        <DialogContent>
          <Typography>
            Mark "{confirmDialog.task?.title}" as done and remove from list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, task: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleMarkAsDone(confirmDialog.task)}
            variant="contained"
            color="success"
          >
            Mark as Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, task: null })}
      >
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.task?.title}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, task: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteTask(deleteDialog.task)}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}